import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useReleases, usePageMeta, EVENT_STATUS_LABEL } from "@/lib/content";
import { fileUrl } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { SpotifyIcon, SoundCloudIcon } from "@/components/site/SocialIcons";

export const SPOTIFY_ARTIST_URL = "https://open.spotify.com/intl-de/artist/6gORyXUlzzV0ILh1tP2ik9";
export const SOUNDCLOUD_ARTIST_URL = "https://soundcloud.com/seimen-dexter";

export default function Music() {
  usePageMeta(
    "Music — SEIMEN DEXTER | Spotify, SoundCloud & Releases",
    "Listen to SEIMEN DEXTER on Spotify and SoundCloud — Hard / Industrial Techno from Mannheim, Germany. Latest releases, sets and streaming links."
  );
  const { data: releases, isLoading } = useReleases();

  return (
    <div className="px-5 md:px-10 pt-28 md:pt-40 pb-24 md:pb-36">
      <Reveal>
        <p className="kicker mb-6">MUSIC</p>
        <h1 className="font-display text-6xl md:text-8xl leading-none mb-4 md:mb-6">MUSIC</h1>
        <p className="kicker">HARD / INDUSTRIAL TECHNO — MANNHEIM</p>
      </Reveal>

      {/* LISTEN TO SEIMEN DEXTER — official streaming profiles */}
      <section data-testid="listen-hub" className="mt-14 md:mt-20">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-8 md:mb-10">
            LISTEN TO
            <br />
            SEIMEN DEXTER.
          </h2>
        </Reveal>
        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <Reveal delay={0.05}>
            <a
              href={SPOTIFY_ARTIST_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="music-spotify-card"
              className="group flex items-center justify-between gap-6 border border-line bg-coal p-7 md:p-10 transition-colors hover:border-white/50"
            >
              <div className="flex items-center gap-6 md:gap-8">
                <span className="text-white group-hover:text-[#1DB954] transition-colors">
                  <SpotifyIcon className="h-12 w-12 md:h-16 md:w-16" />
                </span>
                <div>
                  <p className="font-display text-3xl md:text-4xl leading-none">SPOTIFY</p>
                  <p className="text-[11px] tracking-[0.25em] text-white/40 mt-3">STREAM ALL MUSIC</p>
                  <p className="mt-5 text-[10px] tracking-[0.3em] text-white group-hover:text-bloodbright transition-colors">
                    LISTEN ON SPOTIFY
                  </p>
                </div>
              </div>
              <ArrowUpRight className="h-6 w-6 text-white/40 group-hover:text-white transition-colors shrink-0" />
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={SOUNDCLOUD_ARTIST_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="music-soundcloud-card"
              className="group flex items-center justify-between gap-6 border border-line bg-coal p-7 md:p-10 transition-colors hover:border-white/50"
            >
              <div className="flex items-center gap-6 md:gap-8">
                <span className="text-white group-hover:text-bloodbright transition-colors">
                  <SoundCloudIcon className="h-12 w-12 md:h-16 md:w-16" />
                </span>
                <div>
                  <p className="font-display text-3xl md:text-4xl leading-none">SOUNDCLOUD</p>
                  <p className="text-[11px] tracking-[0.25em] text-white/40 mt-3">LIVE SETS &amp; TRACKS</p>
                  <p className="mt-5 text-[10px] tracking-[0.3em] text-white group-hover:text-bloodbright transition-colors">
                    LISTEN ON SOUNDCLOUD
                  </p>
                </div>
              </div>
              <ArrowUpRight className="h-6 w-6 text-white/40 group-hover:text-white transition-colors shrink-0" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* LATEST RELEASES */}
      <section className="mt-20 md:mt-32">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-10 md:mb-16">
            LATEST RELEASES
          </h2>
        </Reveal>
        {isLoading ? (
          <div className="border-t border-line py-16 text-white/40 text-sm">LOADING…</div>
        ) : releases && releases.length > 0 ? (
          <div data-testid="releases-grid" className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-16">
            {releases.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.06}>
                <Link to={`/music/${r.slug}`} data-testid={`release-card-${r.slug}`} className="group block">
                  <div className="overflow-hidden border border-line relative">
                    {r.cover_url ? (
                      <img
                        src={fileUrl(r.cover_url)}
                        alt={`${r.artist} — ${r.title} cover artwork`}
                        loading="lazy"
                        className="w-full aspect-square object-cover transition-transform duration-1000 group-hover:scale-[1.05]"
                        draggable="false"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-panel flex items-center justify-center">
                        <span className="kicker">NO COVER</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      {r.featured && <span className="badge badge-accent">FEATURED</span>}
                      {r.effective_status === "upcoming" && <span className="badge">{EVENT_STATUS_LABEL.upcoming}</span>}
                    </div>
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-2xl md:text-3xl leading-none truncate">{r.title}</h3>
                      <p className="text-xs text-white/50 mt-2 truncate">
                        {r.artist}
                        {r.label ? ` — ${r.label}` : ""}
                      </p>
                    </div>
                    <span className="text-[10px] tracking-[0.25em] text-white/40 shrink-0 pt-1">
                      {r.effective_status === "upcoming" ? r.release_date : "OUT NOW"}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <div data-testid="music-empty" className="border-t border-line py-16 md:py-20">
            <p className="font-display text-4xl md:text-6xl leading-[0.95]">FIRST RELEASE COMING.</p>
            <p className="kicker mt-4">MUSIC WILL BE ANNOUNCED HERE — LISTEN ON SPOTIFY &amp; SOUNDCLOUD ABOVE</p>
          </div>
        )}
      </section>
    </div>
  );
}
